import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, MapPin, Timer, BadgeCheck } from "lucide-react";
import { MaskedLines, FadeUp } from "./Reveal";
import { BookButton, CallButton, WhatsAppButton } from "./CTA";

const HERO_IMG = "https://images.pexels.com/photos/4098760/pexels-photo-4098760.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TRUST = [
  { icon: BadgeCheck, label: "25+ Years Experience" },
  { icon: MapPin, label: "Delhi NCR" },
  { icon: Timer, label: "Same-Day Service" },
  { icon: ShieldCheck, label: "100% Solution Guaranteed*" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative pt-28 lg:pt-40 pb-16 lg:pb-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <FadeUp className="flex items-center gap-4 mb-7">
            <span className="h-px w-12 bg-gold" />
            <span data-testid="hero-eyebrow" className="text-[11px] tracking-[0.32em] uppercase font-bold text-wine">
              25+ Years of Professional Pest Management
            </span>
          </FadeUp>

          <h1 data-testid="hero-headline" className="font-display font-semibold text-charcoal leading-[1.02] tracking-tight text-5xl sm:text-6xl lg:text-7xl">
            <MaskedLines
              lines={[
                <>Pest Problems?</>,
                <span className="italic text-wine">We'll Find the Source</span>,
                <>&amp; Fix It.</>,
              ]}
              delay={0.15}
              immediate
            />
          </h1>

          <FadeUp delay={0.55} className="mt-7 max-w-xl">
            <p data-testid="hero-subcopy" className="text-base lg:text-lg text-stone2 leading-relaxed">
              Book your <span className="text-charcoal font-semibold">FREE pest inspection</span> today. Same-day service available across Delhi, Gurugram and Noida.
            </p>
          </FadeUp>

          <FadeUp delay={0.7} className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3">
            <BookButton testid="hero-book-btn" />
            <CallButton testid="hero-call-btn" />
            <WhatsAppButton testid="hero-whatsapp-btn" />
          </FadeUp>

          <FadeUp delay={0.85} className="mt-10 grid grid-cols-2 sm:grid-cols-4 border-t border-border pt-6 gap-y-4" >
            {TRUST.map((t) => (
              <div key={t.label} data-testid={`hero-trust-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-2.5 pr-3">
                <t.icon className="h-4 w-4 text-gold shrink-0" strokeWidth={1.75} />
                <span className="text-[12px] font-semibold text-charcoal/80 leading-tight">{t.label}</span>
              </div>
            ))}
          </FadeUp>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <FadeUp delay={0.4}>
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border border-gold/50 pointer-events-none" aria-hidden="true" />
              <div className="relative overflow-hidden aspect-[4/5] bg-sand">
                <motion.img
                  src={HERO_IMG}
                  alt="Professional pest-control technician inspecting a clean, modern home in Delhi NCR"
                  data-testid="hero-image"
                  style={{ y: yImg, scale: 1.12 }}
                  className="w-full h-full object-cover"
                />
              </div>
              <div data-testid="hero-badge" className="absolute -bottom-6 -left-4 sm:-left-8 bg-charcoal text-ivory px-6 py-5 shadow-xl">
                <div className="flex items-center gap-2 mb-1">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
                  </span>
                  <span className="text-[10px] tracking-[0.28em] uppercase font-bold text-gold">Same-Day Service</span>
                </div>
                <p className="font-display text-xl font-medium">Across Delhi NCR</p>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
